import React, { useCallback, useEffect, useRef, useState } from 'react'
import { PlanOption } from './PlanOption'
import { PlanDetailInterface } from './type'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

type PlanDetailProps = {
  item: PlanDetailInterface
  isShowOptions?: boolean
  plansGroup: any[]
}

export const PlanDetail: React.FC<PlanDetailProps> = ({ item, plansGroup, isShowOptions = false }) => {
  const [showOptions, setShowOptions] = React.useState(isShowOptions)
  const [activeDays, setActiveDays] = React.useState<string[]>([])
  const [planOptions, setPlanOptions] = React.useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const listRef = useRef(null)
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const { t } = useTranslation()

  useEffect(() => {
    if (!showOptions || planOptions.length === 0) return
    const containerWrapper = listRef.current
    const items = containerWrapper.querySelectorAll('[data-year]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentYear(Number(entry.target.getAttribute('data-year')))
          }
        })
      },
      {
        root: containerWrapper,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )

    items.forEach((item: Element) => observer.observe(item))
    return () => {
      items.forEach((item: Element) => observer.unobserve(item))
    }
  }, [showOptions, planOptions])

  useEffect(() => {
    if (!showOptions) return
    if (planOptions && planOptions.length > 0) return
    ;(async () => {
      try {
        setLoading(true)
        const planMonth = new URLSearchParams(window.location.search).get('plan_month')
        const url = planMonth
          ? `/plans/${item.plan_id}/check_in_dates?plan_month=${planMonth}`
          : `/plans/${item.plan_id}/check_in_dates`
        const response = await axios.get(url)
        const listDays = response?.data?.sundays || []
        setActiveDays(response?.data?.check_in_dates)
        const updatePlanOption = handleGenerateOptions(listDays)
        setPlanOptions(updatePlanOption)
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    })()
  }, [showOptions])

  const groupSundaysByMonth = useCallback(
    (dates) => {
      const dateObjects = dates.map((dateObj) => ({
        date: new Date(dateObj.date),
        estimation: dateObj.estimation,
      }))

      const monthsMap = {}
      dateObjects.forEach(({ date, estimation }) => {
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const day = date.toISOString().split('T')[0]
        const dayOfMonth = date.getDate()
        const key = `${year}-${month}`

        if (!monthsMap[key]) {
          monthsMap[key] = {
            id: Object.keys(monthsMap).length + 1,
            month: month,
            days: [],
          }
        }
        const value = `${month}/${dayOfMonth}(${t('day.formats.short.sunday')})~`
        monthsMap[key].days.push({
          day: day,
          active: false,
          value: value,
          estimation: estimation ?? '-',
        })
      })

      return Object.values(monthsMap)
    },
    [plansGroup, t],
  )

  const handleGenerateOptions = useCallback((listDays: string[]) => groupSundaysByMonth(listDays), [groupSundaysByMonth])

  const goToPlanDetail = (planId: number, date = '') => {
    window.location.href = date ? `/plans/${planId}?start_date=${date}` : `/plans/${planId}`
  }

  return (
    <div className="plan-details border border-gray-200 rounded-lg shadow-bottom px-4 py-3 mb-4">
      <div className="plan-info flex">
        <div className="plan-thumb w-16 h-16 min-w-16 min-h-16">
          <img className="plan-card rounded-lg w-16 h-16 object-cover" src={item.room_thumbnail_url} alt="Plan Thumbnail" />
        </div>
        <div className="plain-details ml-3 w-3/4">
          <p className="font-bold self-center leading-5 pb-1 text-sm">{item.plan_name}</p>
        </div>
      </div>
      <div className="flex items-center justify-start gap-2 mt-2">
        <span className="text-xs text-gray-800 bg-gray-100 px-3 py-1 rounded-lg plan-detail-room-style">
          {document.documentElement.lang == 'en' ? item.room_type_en : item.room_type}
        </span>
        <span className="ml-1 text-xs text-gray-800 bg-gray-100 px-3 py-1 rounded-lg plan-detail-room-style">
          {t('plan_list.component.capacity.all', { count: item.room_max_count })}
        </span>
      </div>

      <div className="flex">
        <div className="text-block mt-3 mr-2">
          <div className="flex self-center items-center">
            <p className="font-bold leading-4 pb-1 mr-2">
              {t('plan_list.component.currency')} {Number(item.minimum_estimation).toLocaleString()}~
            </p>
          </div>
          <p className="text-xs text-gray-600">{t('plan_list.component.estimate')}</p>
        </div>
        <div className="action ml-auto flex flex-col items-end whitespace-nowrap">
          {item.slot ? (
            <>
              <button
                onClick={() => {
                  goToPlanDetail(item.plan_id)
                }}
                className="all-unset text-sm focus:ring-4 focus:ring-gray-100 text-center font-medium px-2 bg-black text-white mt-1 focus:outline-none rounded-lg md:rounded-lg border hover:bg-gray-600 ml-auto py-2"
                data-id="138"
                type="button"
              >
                {t('plan_list.component.button.select')}
              </button>
              <button onClick={() => setShowOptions((prev) => !prev)} className="flex items-center justify-center mt-2">
                <span
                  className="text-sm pr-1"
                  style={{
                    color: '#007CFF',
                  }}
                >
                  {t('plan_list.component.button.available_date')}
                </span>
                <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12.25 1.55078L7 6.80078L1.75 1.55078"
                    stroke="#6B7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          ) : (
            <span className="px-2 py-2 text-sm font-medium focus:ring-4 focus:ring-gray-100 focus:outline-none rounded-lg border text-gray-500 bg-gray-200 mt-1">
              {t('plan_list.component.button.sold_out')}
            </span>
          )}
        </div>
      </div>
      {showOptions && (
        <div className="flex flex-col items-end gap-4">
          <div className="text-left pb-2 pt-2">{t('plan_list.component.year_section', { year: currentYear })}</div>
          {loading && planOptions.length === 0 && <div className="text-center">{t('plan_list.component.loading')}</div>}
          <div className="flex w-full gap-10px overflow-auto" ref={listRef}>
            {planOptions.map((option) => (
              <div key={option?.id} className="flex flex-col gap-y">
                {option?.days?.map((day: any) => {
                  const isActive = activeDays.includes(day?.day)
                  const year = day.day.split('-')[0]
                  return (
                    <a
                      href="#"
                      onClick={() => goToPlanDetail(item.plan_id, day.day)}
                      data-year={year}
                      key={day?.day}
                      className="pb-4 gap-2"
                    >
                      <PlanOption sale_period={day?.value} estimation={day?.estimation} active={isActive} />
                    </a>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
